/**
 * Angle Role - Validate Pillar
 *
 * Validação de valores de ângulo.
 * ZERO dependências de outros pilares - totalmente standalone.
 *
 * Nota: Ângulos podem ser negativos (rotação anti-horária) e maiores que 360° (múltiplas voltas).
 *
 * @example
 * import { validateAngle, isValidAngle } from '@attrx/role-morphic/angle/validate';
 *
 * validateAngle(90);    // { valid: true, errors: [] }
 * validateAngle(-45);   // { valid: true, errors: [] }
 * validateAngle(720);   // { valid: true, errors: [] }
 * isValidAngle(90);     // true
 */

// =============================================================================
// TYPES
// =============================================================================

export interface AngleValidationConfig {
  /** Valor mínimo permitido */
  min?: number;
  /** Valor máximo permitido */
  max?: number;
  /** Mensagem de erro para valor abaixo do mínimo */
  minError?: string;
  /** Mensagem de erro para valor acima do máximo */
  maxError?: string;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

// =============================================================================
// DEFAULT CONFIG
// =============================================================================

/** Ângulos não têm restrições por padrão - podem ser negativos e > 360° */
export const ANGLE_VALIDATION_CONFIG: AngleValidationConfig = {};

// =============================================================================
// VALIDATE
// =============================================================================

/**
 * Valida um valor de ângulo
 *
 * @param value - Valor a validar
 * @param config - Configuração de validação (opcional)
 * @returns Resultado da validação com lista de erros
 *
 * @example
 * validateAngle(90);                    // { valid: true, errors: [] }
 * validateAngle(-45);                   // { valid: true, errors: [] }
 * validateAngle(90, { max: 180 });      // { valid: true, errors: [] }
 * validateAngle(270, { max: 180 });     // { valid: false, errors: ['...'] }
 */
export function validateAngle(
  value: unknown,
  config: AngleValidationConfig = ANGLE_VALIDATION_CONFIG,
): ValidationResult {
  const errors: string[] = [];

  // Must be a number
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return {
      valid: false,
      errors: ["Value must be a finite number"],
    };
  }

  // Check min
  if (config.min !== undefined && value < config.min) {
    errors.push(config.minError || `Value must be at least ${config.min}`);
  }

  // Check max
  if (config.max !== undefined && value > config.max) {
    errors.push(config.maxError || `Value must be at most ${config.max}`);
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Verifica se valor de ângulo é válido (helper booleano)
 *
 * @param value - Valor a validar
 * @param config - Configuração de validação (opcional)
 * @returns true se válido, false caso contrário
 *
 * @example
 * isValidAngle(90);   // true
 * isValidAngle(-45);  // true
 * isValidAngle(NaN);  // false
 */
export function isValidAngle(
  value: unknown,
  config?: AngleValidationConfig,
): boolean {
  return validateAngle(value, config).valid;
}
